import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  History as HistoryIcon,
  CheckCircle as MilestoneIcon
} from '@mui/icons-material';
import api from '../../utils/api';

interface Milestone {
  key: string;
  label: string;
  at: string;
  detail?: string;
}

interface ClientAuditTimelineProps {
  clientId: string;
}

const ClientAuditTimeline: React.FC<ClientAuditTimelineProps> = ({ clientId }) => {
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/api/clients/${clientId}/milestones`);
        setMilestones(response.data.milestones || []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load audit timeline');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress sx={{ color: '#fff' }} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>;
  }

  if (milestones.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6, color: 'rgba(255,255,255,0.6)' }}>
        <HistoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
        <Typography>No milestones recorded yet</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: 'rgba(255,255,255,0.02)',
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.05)',
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 2, bgcolor: 'rgba(96,165,250,0.05)', borderBottom: '1px solid rgba(96,165,250,0.1)' }}>
        <Typography
          variant="subtitle1"
          sx={{ color: '#60a5fa', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <HistoryIcon fontSize="small" /> Client Milestones
        </Typography>
      </Box>
      <Table size="medium">
        <TableHead>
          <TableRow sx={{ '& th': { borderColor: 'rgba(255,255,255,0.08)' } }}>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Date &amp; Time</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Milestone</TableCell>
            <TableCell sx={{ color: '#94a3b8', fontWeight: 600 }}>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {milestones.map((m, i) => (
            <TableRow
              key={`${m.key}-${i}`}
              sx={{ '&:last-child td': { border: 0 }, '& td': { borderColor: 'rgba(255,255,255,0.05)' } }}
            >
              <TableCell sx={{ color: '#cbd5e1', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                {new Date(m.at).toLocaleString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </TableCell>
              <TableCell sx={{ color: '#fff' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <MilestoneIcon sx={{ color: '#4ade80', fontSize: 18 }} />
                  {m.label}
                </Box>
              </TableCell>
              <TableCell sx={{ color: '#94a3b8' }}>
                {m.detail ? (
                  <Chip
                    label={m.detail}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(148,163,184,0.1)',
                      color: '#cbd5e1',
                      border: '1px solid rgba(148,163,184,0.2)',
                      fontWeight: 500
                    }}
                  />
                ) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ClientAuditTimeline;
